import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType, TextUtils } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { DebugPanel, DebugPanelLocation } from "../../generic-sprites/DebugPanel"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { RadioGroup } from "../../generic-sprites/RadioButtons"
import { Rectangle } from "../../generic-sprites/Shape"
import { AudioSync } from "../../utils/AudioSync"
import { CameraZoom, ZoomInfo } from "../../utils/CameraZoom"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale, x } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { ClassSprite } from "./sprites/ClassSprite"
import { loadAssets, TruthAssets } from "./TruthAssets"
import { TruthPos } from "./TruthPos"
import { truthQuestions } from "./TruthQuestion"

export class TruthOrDareState extends Phaser.State {

    foreground: Phaser.Group
    openingPanel: Rectangle
    panelTextWrap = 0.35
    textPanelX: number
    clazz: ClassSprite
    friendName: string
    relationType: string = StudentStoreSA.instance.isGirl() ? "sister" : "brother"
    roundNum = 0
    panelLineWidth = 4
    undoZoom: ZoomInfo
    debugPanel: DebugPanel

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorLightGray(this.game)
        this.textPanelX = x(0.32)
        this.friendName = StudentStoreSA.instance.isGirl() ? "Marie" : "Joe"
        this.relationType = StudentStoreSA.instance.isGirl() ? "sister" : "brother"

        this.clazz = new ClassSprite(this.game)

        this.setupIntro()
    }

    setupIntro = () => {

        const audioSync = new AudioSync(this.game, TruthAssets.INTRO)

        this.foreground = this.game.add.group()

        Sprites.add(this.game, TruthAssets.WINDOW, {group: this.foreground})

        const introTextGroup = this.game.add.group()

        let timings
        if (StudentStoreSA.instance.isGirl()) {
            timings = {
                friendSlide: 9000,
                truthPanel: 15500,
                embarrassingQuestion: 19500,
                decideGroup: 2500,
                guessHowMany: 10000,
                pickDare: 15500
            }
        } else {
            timings = {
                friendSlide: 9000,
                truthPanel: 15500,
                embarrassingQuestion: 19500,
                decideGroup: 2500,
                guessHowMany: 10000,
                pickDare: 15500
            }
        }

        audioSync.add(timings.friendSlide, () => {
            const user = Sprites.add(this.game, TruthAssets.USER, {group: this.foreground})
            user.x = -1 * user.width
            const friend = Sprites.add(this.game, TruthAssets.FRIEND, {x: TruthPos.friendStartX(), group: this.foreground})
            Sprites.alignWithScreenBottom(user)
            Sprites.alignWithScreenBottom(friend)
            CameraZoom.moveSprite(user, TruthPos.userEndX())
            CameraZoom.moveSprite(friend, TruthPos.friendEndX())
        })

        this.openingPanel = this.makePanel(TruthPos.introBubbleY(), TruthPos.introBubbleHeight(), this.foreground)
        this.openingPanel.graphics.alpha = 0
        const title = PhaserText.create(this.game, "Truth or Dare", {weight: "bold", y: Scale.y(0.2), group: this.foreground})
        title.visible = false

        audioSync.add(timings.truthPanel, () => {
            this.debugPanel.disableSkip()
            this.openingPanel.graphics.alpha = 1
            title.visible = true
            Sprites.centerInWorldXOnly(title)
        })

        const addBullet = this.truthBullets(introTextGroup)

        audioSync.add(timings.embarrassingQuestion, () => addBullet("One person asks the group an embarrassing question"))

        const audioSyncTwo = new AudioSync(this.game, TruthAssets.EXPLANATION)
        audioSync.onStop(audioSyncTwo.start)
        audioSync.start()

        audioSyncTwo.add(timings.decideGroup, () => addBullet("You get to decide whether the group will tell the truth or take a dare"))
        audioSyncTwo.add(timings.guessHowMany, () => addBullet(`You will guess how many of your ${this.relationType}’s friends will raise their hands`))
        audioSyncTwo.add(timings.pickDare, () => addBullet("You get to pick the dare they will take"))

        let youButton
        let friendButton
        let panelText
        const startRoundButton = () => {
            if (youButton) {
                youButton.disable()
            }
            if (friendButton) {
                friendButton.disable()
            }
            audioSync.destroy()
            audioSyncTwo.destroy()
            this.zoomIn(() => {
                if (youButton) {
                    youButton.destroy()
                }
                if (friendButton) {
                    friendButton.destroy()
                }
                if (panelText) {
                    panelText.destroy()
                }
                this.startRound()
            })
        }

        audioSyncTwo.onStop(() => {
            const chooseAudio = this.game.add.audio(TruthAssets.CHOOSE_FIRST_PLAYER)
            chooseAudio.play()
            introTextGroup.destroy()
            panelText = this.makePanelText(
                `Do you want to choose "Truth or Dare" first, or do you want ${this.friendName} to go first?`,
                undefined,
                this.foreground
            )
            const buttonHeight = Scale.y(0.6)

            youButton = Buttons.create(
                this.game,
                "You",
                startRoundButton,
                {y: buttonHeight, group: this.foreground, enabled: false, buttonType: ButtonType.PRIMARY}
            )
            friendButton = Buttons.create(this.game, this.friendName, () => {
                this.roundNum++
                startRoundButton()
            }, {y: buttonHeight, group: this.foreground, enabled: false, buttonType: ButtonType.PRIMARY})
            Sprites.evenlySpaceHorizontally(this.openingPanel.invisibleSprite, youButton.button, friendButton.button)
            youButton.center()
            friendButton.center()
            chooseAudio.onStop.addOnce(() => {
                youButton.enable()
                friendButton.enable()
            })
        })
        this.debugPanel = new DebugPanel(this.game, startRoundButton, DebugPanelLocation.LOWER_LEFT)
    }

    zoomIn = (onComplete: () => void) => {
        log.debug("Performing zoom in on class in truth or dare.")
        const target = {
            x: Scale.x(0.03),
            y: Scale.y(0.03),
            width: Scale.x(0.94),
            height: Scale.y(0.94),
        }
        this.undoZoom = CameraZoom.zoom(this.game, target, 500)
        if (this.foreground) {
            Sprites.fadeGroup(this.foreground, {
                fadeIn: false, onComplete
            })
        }
    }

    zoomOut = (onComplete: () => void) => {
        log.debug("Performing zoom in out class in truth or dare.")

        CameraZoom.performZoom(this.undoZoom)
        if (this.foreground) {
            Sprites.fadeGroup(this.foreground, {
                fadeIn: true, onComplete
            })
        }
    }

    startRound = () => {
        this.roundNum++
        const roundNum = this.roundNum
        if (roundNum > truthQuestions.length) {
            this.finishGame()
            return
        }

        this.debugPanel.setSkip(() => {
            this.roundNum++
        }, false, "Increment Round #")

        log.debug(`Starting round with number ${roundNum}`)
        const roundInfo = truthQuestions[roundNum - 1]
        log.debug(`Got round info for num ${roundNum} info: ${CCUtils.prettyJson(roundInfo)}`)

        this.leaderBubble(TruthAssets.QUESTION + roundNum, roundInfo.question + " Truth or dare?", true, () => {

            const choiceGroup = this.game.add.group()
            const panel = this.makePanel(undefined, undefined, choiceGroup)

            const chooseTruth = () => {
                choiceGroup.destroy()
                this.truth(roundNum)
            }
            if (roundInfo.friendQuestion) {

                const friendsTurnToChoose = this.makePanelText(`Now it's ${this.friendName}'s turn to choose "Truth or Dare."`)
                const friendsTurn = this.game.add.audio(TruthAssets.FRIENDS_TURN_TO_PICK_TRUTH_OR_DARE)
                friendsTurn.play()

                friendsTurn.onStop.addOnce(() => {
                    friendsTurnToChoose.destroy()
                    PhaserText.create(
                        this.game,
                        `${this.friendName} wants the group to answer truthfully.`,
                        {x: this.textPanelX, wordWrapWidth: 0.2, y: Scale.y(0.48), fontPx: 16, group: choiceGroup}
                    )
                    const truthButton = Buttons.create(
                        this.game,
                        "Next",
                        chooseTruth,
                        {x: this.textPanelX, y: Scale.y(0.61), enabled: false, group: choiceGroup, buttonType: ButtonType.PRIMARY}
                    )

                    const friend = Sprites.add(this.game, TruthAssets.FRIEND, {group: choiceGroup})
                    const friendScale = panel.graphics.height / friend.height * 0.75
                    Scale.scaleSprite(friend, friendScale)
                    Sprites.bottomAlign(panel.graphics, friend)
                    Sprites.rightAlign(panel.graphics, friend)
                    friend.y -= this.panelLineWidth * 1.5
                    friend.x -= Scale.x(0.02)
                    const truthBubble = Sprites.add(this.game, TruthAssets.SPEECH_BUBBLE,
                        {x: this.textPanelX, y: panel.graphics.y + Scale.y(0.02), group: choiceGroup})
                    Scale.scaleSprite(truthBubble, 0.55)
                    const truthText = PhaserText.create(
                        this.game,
                        "TRUTH!",
                        {group: choiceGroup, type: FontType.HAND_WRITING}
                    )
                    Sprites.centerSpriteInSprite(truthBubble, truthText)
                    truthText.y = truthBubble.y + Scale.y(0.035)

                    const friendChoose = this.game.add.audio(TruthAssets.FRIEND_WANTS_TRUTH)
                    friendChoose.play()
                    friendChoose.onStop.addOnce(() => {
                        truthButton.enable()
                    })
                })

            } else {
                const text = this.makePanelText(`It's your turn.`, undefined, choiceGroup)
                Sprites.centerInWorldXOnly(text)
                this.makePanelText(
                    `Do you want your ${this.relationType}'s friends to tell the truth or take a dare?`,
                    Scale.y(0.42),
                    choiceGroup
                )
                const buttonY = Scale.y(0.56)
                const truthButton = Buttons.create(
                    this.game,
                    "Truth",
                    chooseTruth,
                    {y: buttonY, enabled: false, group: choiceGroup, buttonType: ButtonType.PRIMARY}
                )
                const dareButton = Buttons.create(this.game, "Dare", () => {
                    choiceGroup.destroy()
                    this.dare()
                }, {y: buttonY, enabled: false, group: choiceGroup, buttonType: ButtonType.PRIMARY})
                Sprites.evenlySpaceHorizontally(panel.invisibleSprite, truthButton.button, dareButton.button)

                truthButton.center()
                dareButton.center()

                const choose = this.game.add.audio(TruthAssets.CHOOSE_TRUTH_OR_DARE)
                choose.play()
                choose.onStop.addOnce(() => {
                    truthButton.enable()
                    dareButton.enable()
                })
            }
        })
    }

    truth = (roundNum: number) => {
        const {question, instructions, headSymbol} = truthQuestions[roundNum - 1]
        this.classBubble(TruthAssets.TRUTH, () => {

            const questionX = Scale.x(0.07)
            const fontPx = 16
            const questionSprite = PhaserText.create(this.game, question, {x: questionX, y: Scale.y(0.04), fontPx})
            const instructionsSprite = PhaserText.create(this.game, instructions, {x: questionX, y: Scale.y(0.09), wordWrapWidth: 0.68, fontPx})

            let currentGuess: Phaser.Text
            let headsClicked = 0
            const choose = this.game.add.audio(TruthAssets.QUESTION_PICKED_TRUTH + roundNum)
            choose.play()
            choose.onStop.addOnce(() => {
                const next = this.nextInUpperRight(() => {
                    this.clazz.demakeHeads()
                    this.answered(headsClicked, roundNum)
                    questionSprite.destroy()
                    instructionsSprite.destroy()
                    zero.destroy()
                    next.destroy()
                    currentGuess.destroy()
                })
                next.disable()
                const zero = Buttons.create(
                    this.game,
                    "0",
                    () => {
                        this.clazz.clearClickedHeads()
                        next.enable()
                    },
                    {
                        x: next.button.x, y: next.button.y + next.button.height + Scale.y(0.02), buttonType: ButtonType.PRIMARY,
                        buttonSize: ButtonSize.SQUARE
                    }
                )
                const addYourGuess = (guess: number) => {
                    if (currentGuess) {
                        currentGuess.destroy()
                    }
                    currentGuess = PhaserText.create(
                        this.game,
                        `Your guess:  ${guess} teen${TextUtils.plural(guess)}`,
                        {x: next.button.x, y: zero.button.y + zero.button.height + Scale.y(0.02), fontPx, wordWrapWidth: 0.2}
                    )
                }
                addYourGuess(0)
                this.clazz.makeHeadsClickable(headSymbol)
                this.clazz.onHeadClicked = (numHeads) => {
                    log.debug(`Clicked a head, num heads: ${numHeads}`)
                    headsClicked = numHeads
                    addYourGuess(numHeads)
                    next.enable()
                }
            })
        })
    }

    answered = (answer: number, roundNum: number) => {
        log.debug(`Student answered ${answer}`)
        const {correctAnswer, raiseHands} = truthQuestions[roundNum - 1]
        this.leaderBubble(TruthAssets.RAISE_HANDS + roundNum, raiseHands, false, () => {
            if (correctAnswer === 1) {
                this.clazz.oneHand()
            } else {
                this.clazz.twoHands()
            }
            const yourGuess = PhaserText.create(this.game, `Your guess:  ${answer} teen${TextUtils.plural(answer)}`,
                {fontPx: 16, x: Scale.x(0.7), y: Scale.y(0.05)})

            let audio
            let right
            if (answer < correctAnswer) {
                audio = TruthAssets.QUESTION_UNDERESTIMATE
                right = "You underestimated!"
            } else if (answer === correctAnswer) {
                audio = TruthAssets.QUESTION_CORRECT
                right = "You're right!"
            } else {
                audio = TruthAssets.QUESTION_OVERESTIMATE
                right = "Your guess is too high."
            }
            const sync = new AudioSync(this.game, audio + roundNum)

            let truth
            sync.add(1000, () => {
                truth = PhaserText.create(this.game, `Truth  ${correctAnswer} teen${TextUtils.plural(correctAnswer)}`,
                    {fontPx: 16, y: Scale.y(0.1), color: COLOR.red})
                Sprites.rightAlign(yourGuess, truth)
            })

            let correctness
            sync.add(2000, () => {
                correctness = PhaserText.create(this.game, right, {fontPx: 16, y: Scale.y(0.15), color: COLOR.red})
                Sprites.rightAlign(yourGuess, correctness)
            })

            sync.onStop(() => {
                const next = this.nextInUpperRight(() => {
                    yourGuess.destroy()
                    if (truth) {
                        truth.destroy()
                    }
                    if (correctness) {
                        correctness.destroy()
                    }
                    this.clazz.resetClassImage()
                    this.startRound()
                })
                Sprites.rightAlign(yourGuess, next.button)
                next.button.y = Scale.y(0.2)
                next.center()
            })

            sync.start()
        })
    }

    dare = () => {
        this.classBubble(TruthAssets.DARE, () => {
            const panel = this.makePanel()
            const text = this.makePanelText("Which dare do you want the teens to take?")
            const nextButton = Buttons.create(this.game, "Next", undefined, {y: Scale.y(0.61), buttonType: ButtonType.PRIMARY})
            nextButton.disable()
            nextButton.centerInWorldX()

            const radioGroup = new RadioGroup(
                this.game,
                this.textPanelX,
                Scale.y(0.46),
                [
                    {text: "Wear silly hats", audioKey: TruthAssets.DARE_HATS},
                    {text: "Do a hand stand", audioKey: TruthAssets.DARE_HANDSTAND},
                    {text: "Kiss the person next to them", audioKey: TruthAssets.DARE_KISS}
                ],
                () => nextButton.enable(radioGroup.currentlySelected != null),
                false
            )
            nextButton.setOnClick(() => {
                log.debug("In on click of next button for silly dare.")
                const selected = radioGroup.currentlySelected
                if (selected != null) {
                    if (selected === "Wear silly hats") {
                        this.clazz.sillyHats()
                    } else if (selected === "Do a hand stand") {
                        this.clazz.handStand()
                    } else {
                        this.clazz.kissing()
                    }
                }
                panel.graphics.destroy()
                text.destroy()
                nextButton.destroy()
                radioGroup.group.destroy()
                const laughing = this.game.add.audio(TruthAssets.KIDS_LAUGHING)
                laughing.play()
                laughing.onStop.addOnce(() => {
                    const nextQuestion = this.game.add.audio(TruthAssets.NEXT_QUESTION)
                    nextQuestion.play()
                    nextQuestion.onStop.addOnce(() => {
                        this.nextInUpperRight(() => {
                            this.clazz.resetClassImage()
                            nextButton.destroy()
                            this.startRound()
                        })
                    })
                })
            })

            const pickedDare = this.game.add.audio(TruthAssets.YOU_PICKED_DARE)
            pickedDare.play()
            pickedDare.onStop.addOnce(() => {
                radioGroup.enable()
            })
        })
    }

    finishGame = () => {

        const panel = this.makePanel(undefined, Scale.y(0.2))
        const panelText = this.makePanelText("Party's Over")
        panelText.fontSize = 36
        Sprites.centerInWorldXOnly(panelText)
        const timer = this.game.time.create()
        timer.add(1500, () => {
            panel.destroy()
            panelText.destroy()
            this.openingPanel.invisibleSprite.destroy()

            this.zoomOut(() => {
                const sync = new AudioSync(this.game, TruthAssets.OUTRO)

                const addBullet = this.truthBullets()
                addBullet("Kids overestimate what other kids think and do.")
                sync.add(4000, () => addBullet("Overestimating can get you into trouble."))
                let lastBullet
                sync.add(8000, () => {
                    lastBullet = addBullet("Overestimating can get you to try smoking or vaping.")
                })

                sync.onStop(() => {
                    const doneButton = Buttons.create(
                        this.game,
                        "Next",
                        () => {
                            Transitions.startOutro(this.game, ActivityKey.TRUTH_OR_DARE)
                        },
                        {y: lastBullet?.y + lastBullet?.height + Scale.y(0.03), buttonType: ButtonType.PRIMARY, buttonSize: ButtonSize.WIDE}
                    )
                    Sprites.centerInWorldXOnly(doneButton.button)
                    doneButton.center()
                })

                sync.start()
            })
        })
        timer.start()
    }

    classBubble = (audioAsset: string, onComplete?: () => void) => {
        const bubble = Sprites.add(this.game, TruthAssets.CLASS_BUBBLE, {y: Scale.y(0.03)})
        Scale.scaleSprite(bubble, 0.78)
        Sprites.centerInWorldXOnly(bubble)
        const text = PhaserText.create(this.game, audioAsset === TruthAssets.TRUTH ? "TRUTH!" : "DARE!", {y: Scale.y(0.07), type: FontType.HAND_WRITING})
        Sprites.centerInWorldXOnly(text)
        const audio = this.game.add.audio(audioAsset)
        audio.play()
        audio.onStop.addOnce(() => {
            bubble.destroy()
            text.destroy()
            if (onComplete) {
                onComplete()
            }
        })
    }

    leaderBubble = (audioAsset: string, leaderText: string, short: boolean, onComplete?: () => void) => {
        const bubble = Sprites.add(this.game, TruthAssets.SPEECH_BUBBLE_LEADER, {x: Scale.x(0.2), y: Scale.y(-0.02)})
        Scale.scaleSprite(bubble, 0.8)
        const text = PhaserText.create(
            this.game,
            leaderText,
            {x: Scale.x(0.25), y: short ? Scale.y(0.06) : Scale.y(0.04), wordWrapWidth: 0.45, fontPx: 17}
        )
        const audio = this.game.add.audio(audioAsset)
        audio.play()
        audio.onStop.addOnce(() => {
            bubble.destroy()
            text.destroy()
            if (onComplete) {
                onComplete()
            }
        })
    }

    makePanel = (y: number = Scale.y(0.28), height: number = Scale.y(0.4), group?: Phaser.Group) => {
        return new Rectangle(
            this.game,
            TruthPos.introBubbleX(),
            y,
            TruthPos.introBubbleWidth(),
            height,
            {
                lineColor: colorNum.green,
                fillColor: colorNum.lightGreen,
                cornerRounding: 20,
                lineWidth: this.panelLineWidth,
                group
            }
        )
    }

    makePanelText = (text: string, y: number = Scale.y(0.35), group?: Phaser.Group) => {
        return PhaserText.create(this.game, text, {x: this.textPanelX, y, fontPx: 18, wordWrapWidth: this.panelTextWrap, group})
    }

    nextInUpperRight = (onClick: () => void) => {
        const nextButton = Buttons.create(this.game, "Next", () => {
            nextButton.destroy()
            onClick()
        }, {x: Scale.x(0.77), y: Scale.y(0.05), buttonType: ButtonType.PRIMARY})
        return nextButton
    }

    truthBullets = (group?: Phaser.Group) => {
        return PhaserText.bulletList(
            this.game,
            {x: this.textPanelX, y: Scale.y(0.28), fontPx: 18, wordWrapWidth: this.panelTextWrap, group}
        )
    }

}
